import React from "react"

export type SrcSets = {
  sm: string
  md?: string | null | undefined
  lg?: string | null | undefined
  xl?: string | null | undefined
}

interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  srcSetUrls: SrcSets
  sizes?: string // allow custom sizes if needed
  loading?: "eager" | "lazy" // optional loading prop for flexibility
}

type Size = "sm" | "md" | "lg" | "xl"

const defaultSizes = {
  sm: 640,
  md: 900,
  lg: 1024,
  xl: 1280,
}

export const Image = React.forwardRef<HTMLImageElement, Props>(
  (
    {
      srcSetUrls,
      alt = "", // default alt for accessibility
      className,
      sizes = "(max-width: 640px) 640px, (max-width: 900px) 900px, (max-width: 1024px) 1024px, 1280px", // default sizes
      loading = "lazy", // lazy loading by default
      ...rest
    },
    ref
  ) => {
    const initialSrc = srcSetUrls.sm
    if (!initialSrc) return null // Ensure initialSrc is defined

    // Filter out undefined or null entries from srcSetUrls
    const srcSet = Object.entries(srcSetUrls)
      .filter(([, url]) => url) // remove any undefined or null entries
      .map(([size, url]) => `${url} ${defaultSizes[size as Size]}w`)

    return (
      <img
        ref={ref} // Forward the ref
        className={className}
        src={initialSrc}
        srcSet={srcSet.length > 1 ? srcSet.join(", ") : undefined} // Only add srcSet if there are multiple sources
        sizes={srcSet.length > 1 ? sizes : undefined} // Only add sizes if srcSet has multiple entries
        alt={alt}
        loading={loading}
        onContextMenu={e => {
          e.preventDefault()
        }}
        {...rest}
      />
    )
  }
)

Image.displayName = "Image" // Set displayName for easier debugging
